import React, { useState } from 'react';
import { Button, Form } from 'antd';
import { useTranslation } from 'react-i18next';
import './PasswordCreateOrReset.scss';
import PasswordCreationForm from 'components/passwordCreationForm/PasswordCreationForm';
import NeedHelp from 'components/needHelp/NeedHelp';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  buildEmailLinkExpiredPath,
  buildPasswordCreateSuccessPath,
  buildPasswordResetSuccessPath,
} from 'utils/pathBuilder';
import CarouselContainer from 'components/carouselContainer/CarouselContainer';
import { createPasswordForVerifiedUser } from 'services/registrationService';
import { showErrorNotification } from 'utils/commonUtils';
import { resetPassword } from 'services/loginService';
import { useSession } from 'context/SessionContext';
import { SessionDTO } from 'types/dto/login';

type PasswordCreateOrResetProps = {
  type: 'create' | 'reset';
};

const PasswordCreateOrReset: React.FC<PasswordCreateOrResetProps> = ({type}) => {
  const { t } = useTranslation('password_create_or_reset');
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { session, setSession } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('reset_token');

  const handleConfirm = async () => {
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      // console.log('Password creation data:', values);

      //Call API to create password or reset password
      try {
        setLoading(true);
        if (type === 'create') {
          const sessionDTO = await createPasswordForVerifiedUser(
            values.tempToken,
            values.password,
            values.confirmPassword,
          );
          setSession(sessionDTO as SessionDTO);
          //Navigate to next page or show success message
          navigate(buildPasswordCreateSuccessPath());
        } else {
          if (!token) {
            navigate(buildEmailLinkExpiredPath());
            return;
          }
          //call reset password api, backend needs to return sessionDTO
          const accessToken = session?._tk || '';
          await resetPassword(values.confirmPassword, token, accessToken);
          // setSession(null);

          navigate(buildPasswordResetSuccessPath());
        }
      } catch (error: unknown) {
        showErrorNotification(
          type === 'create'
            ? t('passwordCreationFailed')
            : t('passwordResetFailed'),
          error as Error,
        );
      } finally {
        setLoading(false);
      }
    } catch (error) {
      console.error('validation failed:', error);
    }
  };

  return (
    <CarouselContainer>
      {/* Right side - Password Creation Form */}
      <div className="password-create-or-reset-form-section">
        <div className="form-content">
          <div className="vls-section-title">
            {type === 'create'
              ? t('passwordCreation.title')
              : t('passwordReset.title')}
          </div>

          <Form form={form} layout="vertical" className="password-form" onFinish={handleConfirm}>
            <PasswordCreationForm />

            <Button
              type="primary"
              block
              htmlType="submit"
              loading={loading}
              className="confirm-button"
            >
              {t('passwordCreation.confirm')}
            </Button>
          </Form>

          <NeedHelp />
        </div>
      </div>
    </CarouselContainer>
  );
};

export default PasswordCreateOrReset;
